import React, { useMemo } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import { notAvailableYetHint } from '@/lib/notAvailableYet';
import type { MerchantConnectionsApi } from '@/hooks/useMerchantConnections';
import { ConnectionsOwner, FeedConnection, formatFeedDate, groupMerchantFeeds } from '@/shared/merchantFeeds';
import { FeedList } from './feeds/FeedList';
import { SettingsSection } from './SettingsGroup';

// Licensed Seller Monitoring (FIG-323 / REQ-056, REQ-057). The merchant's own
// bank accounts (Plaid) and accounting software (Fivetran), which EVE uses to
// confirm the merchant stays solvent and in good standing. "Connected by", the
// "Ask merchant to connect" emails and View in Plaid need UC-221; sharing a
// parent's accounting connection needs UC-219.

interface LicensedSellerMonitoringSectionProps {
  merchant: ConnectionsOwner;
  connections: MerchantConnectionsApi;
  canEdit: boolean;
}

const ASK_USE_CASE = 'UC-221';

/** "Chase · •••• 1234, •••• 5678 · connected Oct 2, 2026" from what Plaid stored. */
export function describeBankAccount(feed: FeedConnection): string {
  const accounts = feed.value.metadata?.accounts ?? [];
  const masks = accounts
    .map((account) => (account?.mask ? `•••• ${account.mask}` : null))
    .filter((mask): mask is string => !!mask);
  const connected = feed.value.created_at ? `connected ${formatFeedDate(feed.value.created_at)}` : null;
  return [masks.join(', ') || null, connected].filter(Boolean).join(' · ') || 'Bank account';
}

const AskButton: React.FC<{ label: string }> = ({ label }) => (
  <Button
    type="button"
    variant="outline"
    size="sm"
    disabled
    className="gap-1.5"
    aria-label={`${label}. ${notAvailableYetHint(ASK_USE_CASE)}`}
    title={notAvailableYetHint(ASK_USE_CASE)}
  >
    <Send className="h-4 w-4" aria-hidden="true" />
    {label}
  </Button>
);

export const LicensedSellerMonitoringSection: React.FC<LicensedSellerMonitoringSectionProps> = ({
  merchant,
  connections,
  canEdit,
}) => {
  const feeds = useMemo(() => groupMerchantFeeds(merchant.connections), [merchant.connections]);
  const listProps = { owner: merchant, connections, canEdit };

  return (
    <TooltipProvider>
      <SettingsSection
        title="Merchant Bank Accounts"
        description="The merchant's own bank accounts, connected by the merchant when they sign in to their bank through Plaid. EVE uses them to confirm the merchant remains solvent, a condition of its limited right to repurchase and sell."
      >
        <FeedList
          ariaLabel="Merchant bank accounts"
          feeds={feeds.bank}
          detailFor={describeBankAccount}
          emptyText="No merchant bank accounts connected."
          {...listProps}
        />
        {canEdit && (
          <div className="mt-3">
            <AskButton label="Ask merchant to connect a bank" />
          </div>
        )}
      </SettingsSection>

      <SettingsSection
        title="Merchant Accounting"
        description="The merchant's accounting software, connected through Fivetran (for example QuickBooks Online, NetSuite or Xero). The merchant authorizes the connection. EVE uses it, with the bank accounts, to confirm the merchant remains in good standing."
      >
        <FeedList
          ariaLabel="Merchant accounting"
          feeds={feeds.accounting}
          detailFor={() => 'Accounting'}
          emptyText="No accounting software connected."
          {...listProps}
        />
        {canEdit && (
          <div className="mt-3">
            <AskButton label="Ask merchant to connect accounting software" />
          </div>
        )}
      </SettingsSection>
    </TooltipProvider>
  );
};

export default LicensedSellerMonitoringSection;
